/**
 * THE MAP'S GEOMETRY — no canvas, no DOM, no framework.
 *
 * The web draws with Canvas 2D and a mobile app would draw with Skia. Neither of those is
 * where the thinking is. What to frame, what a tap landed on, which names will fit without
 * colliding, how loud a line should be — all of it is arithmetic over positions, and all of
 * it was buried in the middle of a 1,957-line browser file where a second renderer could
 * only have copied it.
 *
 * Every rule here was arrived at by looking at the thing on a screen and finding it wrong.
 * The comments say which, because the numbers look arbitrary otherwise and someone will
 * eventually be tempted to round them off.
 *
 * Compiled once and loaded two ways: require() in Node, the checks and the server, and import in
 * a bundler for the web. One CommonJS build serves both, so the geometry lives in one place rather
 * than in a copy per renderer.
 */
/** A circle on the map: where it is, how big, and what it belongs to. */
export interface Placed {
  id: string;
  x: number;
  y: number;
  /** Layout radius, before the camera scales it. */
  r: number;
  label: string;
}

export interface Camera {
  x: number;
  y: number;
  z: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * The span of the middle `keep` of a set of values.
 *
 * A force layout pushes an unconnected name outward with nothing pulling it back, so a
 * dozen strays end up thousands of units from anything. Framing the full bounding box let
 * those few decide the view: the box was 5,017 units wide when nearly every circle lived
 * inside a fifth of that, and on a phone the substance of the map filled 24% of the screen.
 *
 * Trimming the extremes frames where the mass actually is. The outliers are still drawn and
 * still reachable by panning — they simply stop dictating how small everything else has to be.
 */
export function spanOfMass(values: number[], keep = 0.96): [number, number] {
  if (!values.length) return [0, 1];
  const s = [...values].sort((a, b) => a - b);
  const cut = (1 - keep) / 2;
  const lo = s[Math.floor(cut * (s.length - 1))];
  const hi = s[Math.ceil((1 - cut) * (s.length - 1))];
  return hi > lo ? [lo, hi] : [s[0], s[s.length - 1] ?? s[0] + 1];
}

export interface FrameOptions {
  /** Room for the controls above the map. */
  topBar?: number;
  /** Room for the legend below it. */
  footer?: number;
  /** How much of the mass to frame; the rest may fall outside. */
  keep?: number;
  /** Never zoom in past this, however tight the cluster. */
  maxZoom?: number;
}

/**
 * Where the camera has to sit for the map to fill the screen it has been given.
 *
 * The margin is a share of the viewport rather than a fixed number of pixels — 70px was a
 * tenth of a phone and a twentieth of a laptop, which is not the same margin.
 */
export function frame(
  /*
   * ONLY WHAT IT READS: two coordinates, and it may not keep them.
   *
   * It asked for `Placed[]` and used `p.x` and `p.y`, so every caller had to own a full node —
   * label, radius and all — and a mutable array of them. That broke the delivery measurements the
   * day `Placed` gained a `label` the map draws and a measurement has no use for, in a tool no
   * pre-commit typecheck covers. `toScreen` directly below has always asked this way; this is that
   * convention applied to its neighbour, and it widens what is accepted without changing an answer.
   */
  points: readonly { x: number; y: number }[],
  view: Viewport,
  opts: FrameOptions = {},
): Camera {
  const { width: W, height: H } = view;
  const topBar = opts.topBar ?? (W < 700 ? 120 : 150);
  const footer = opts.footer ?? 46;
  const keep = opts.keep ?? 0.96;
  const maxZoom = opts.maxZoom ?? 1.5;

  if (!points.length) return { x: 0, y: 0, z: 1 };

  const [minX, maxX] = spanOfMass(points.map(p => p.x), keep);
  const [minY, maxY] = spanOfMass(points.map(p => p.y), keep);

  const pad = Math.max(18, Math.min(W, H) * 0.05);
  const zx = (W - pad * 2) / (maxX - minX || 1);
  const zy = (H - topBar - footer - pad) / (maxY - minY || 1);
  const z = Math.min(zx, zy, maxZoom);

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2 - ((topBar - footer) / 2) / z,
    z,
  };
}

/** Where a point lands on screen, under a camera. */
export function toScreen(p: { x: number; y: number }, cam: Camera, view: Viewport) {
  return {
    x: (p.x - cam.x) * cam.z + view.width / 2,
    y: (p.y - cam.y) * cam.z + view.height / 2,
  };
}

/**
 * What a tap at (sx, sy) landed on, or null.
 *
 * Deliberately forgiving: a finger is wider than a three-pixel dot, so the reach is at
 * least 26px regardless of how small the circle is drawn. Bigger circles claim a
 * proportionally bigger area, and where two are in reach the nearer wins.
 */
export function hit(
  points: Placed[],
  sx: number,
  sy: number,
  cam: Camera,
  view: Viewport,
  reach = 26,
): Placed | null {
  let best: Placed | null = null;
  let bd = reach;
  for (const g of points) {
    const p = toScreen(g, cam, view);
    const d = Math.hypot(p.x - sx, p.y - sy);
    const own = g.r * cam.z;
    if (d < Math.max(bd, own + 12) && d < bd + own) { best = g; bd = d; }
  }
  return best;
}

/**
 * How loud a tie should be drawn.
 *
 * Two figures share anywhere between 3 verses and 1,611, and every line was drawn
 * identically — so the pair named together sixteen hundred times looked exactly like the
 * pair that coincide three times, and 2,762 equal strokes read as one grey web.
 *
 * Log, not linear, or the top tie would be three hundred times any other and everything
 * else would vanish. Length is discounted separately: an edge that crosses the whole map
 * reaches an outlier the layout could not pull in, which is the weakest kind of link and
 * the most visually expensive, cutting across every cluster on its way.
 */
export function edgeWeight(sharedPassages: number, screenLength: number, view: Viewport) {
  const strength = Math.min(1, Math.log(Math.max(sharedPassages, 1)) / Math.log(150));
  const reach = Math.max(1, Math.min(view.width, view.height) * 0.55);
  const near = 1 / (1 + Math.pow(screenLength / reach, 2));
  return {
    strength,
    /** 0–1: multiply into whatever opacity the renderer uses. */
    presence: (0.25 + 0.75 * strength) * (0.35 + 0.65 * near),
    width: 0.7 + 1.1 * strength,
  };
}

export interface LabelBox { x0: number; x1: number; y0: number; y1: number }
export interface LabelCandidate {
  id: string;
  text: string;
  /** Screen position of the circle this names. */
  x: number;
  y: number;
  /** On-screen radius of that circle. */
  r: number;
  /** Never suppress this one — the reader asked for it. */
  pinned?: boolean;
}

export interface PlacedLabel extends LabelCandidate { box: LabelBox }

/**
 * Which names fit, given who else is on screen.
 *
 * The busiest figures sit closest together — that is what makes them busy — so every label
 * the map most wanted to show was drawn on top of the others: Solomon, Jesus, Abraham and a
 * dozen more collided into a smear at the centre where not one could be read.
 *
 * Two rules, both learned by looking:
 *
 *  - A label is placed only if its box is clear of every box already placed. Candidates
 *    arrive largest first, so where two compete the more prominent figure keeps its name.
 *  - It must also clear the CIRCLES — but only circles worth not covering. Testing against
 *    every circle left the whole map with two labels, because the dense centre is mostly
 *    dust two or three pixels across, which the halo carries text over perfectly well.
 *
 * Both tests need every circle known up front, which is why this takes the full set rather
 * than being folded into a drawing loop: run inside one, a label is tested only against
 * what happens to have been drawn already, and the biggest names — placed first, on a
 * nearly empty canvas — end up crossed by everything drawn after them.
 */
export function placeLabels(
  candidates: LabelCandidate[],
  circles: { x: number; y: number; r: number }[],
  measure: (text: string) => number,
  opts: { minBlockingRadius?: number; rightEdge?: number; blocked?: readonly LabelBox[] } = {},
): PlacedLabel[] {
  const minBlocking = opts.minBlockingRadius ?? 7;
  const rightEdge = opts.rightEdge ?? Infinity;
  /*
   * Regions the map is drawn UNDER — the overlay, the orientation pill, the hint. The canvas fills
   * the viewport and the chrome floats over it, so a name placed beneath one is drawn and then
   * covered: ink spent on something no reader can read, and worse, a name that LOOKS placed to the
   * placer, so the figure that could have been named there loses its turn to a ghost.
   *
   * Passed in rather than measured here, because only the main thread can measure the DOM and this
   * runs in the worker as well.
   */
  const blocked = opts.blocked ?? [];
  const blockers = circles.filter(c => c.r >= minBlocking);

  const placed: PlacedLabel[] = [];
  const boxes: LabelBox[] = [];

  /* A label box centred on the node, at a baseline `ly`. Roomier than the glyphs, because names that
     touch only at the corners are technically not overlapping and still read as a jumble. */
  const boxAt = (c: LabelCandidate, ly: number): LabelBox => {
    const half = measure(c.text) / 2 + 6;
    return { x0: c.x - half, x1: c.x + half, y0: ly - 13, y1: ly + 6 };
  };
  const clear = (box: LabelBox): boolean => {
    if (box.x1 > rightEdge) return false; // a name the panel would cut in half is not a name
    if (blocked.some(o => box.x0 < o.x1 && box.x1 > o.x0 && box.y0 < o.y1 && box.y1 > o.y0)) return false;
    if (blockers.some(o => box.x0 < o.x + o.r && box.x1 > o.x - o.r && box.y0 < o.y + o.r && box.y1 > o.y - o.r)) return false;
    if (boxes.some(o => box.x0 < o.x1 && box.x1 > o.x0 && box.y0 < o.y1 && box.y1 > o.y0)) return false;
    return true;
  };
  const take = (c: LabelCandidate, box: LabelBox): void => { boxes.push(box); placed.push({ ...c, box }); };

  for (const c of candidates) {
    const above = boxAt(c, c.y - c.r - 6);
    if (c.pinned) { take(c, above); continue; } // the reader asked for it — always named, above
    /* Prefer the name ABOVE its node; if that space is taken — the usual case for two nodes sitting
       close together — drop it BELOW instead, so both are named rather than one silently losing out.
       Only when neither the space above nor below is clear does the name yield. */
    if (clear(above)) { take(c, above); continue; }
    const below = boxAt(c, c.y + c.r + 19);
    if (clear(below)) take(c, below);
  }
  return placed;
}
